/* oxlint-disable react/static-components -- these render APIs intentionally receive component references */
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/solid'
import classNames from 'classnames'
import { Page as PageIcon } from 'iconoir-react'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import ProgressBar from './ProgressBar'
import { formatDate } from 'app/date.utils'
import { notifyError } from 'app/services/notify.client'

export type FileProps = {
  id?: string
  src?: string
  date?: string
  name?: string
  size?: number
}

export type FileUploaderProps = {
  allowedFiles: string[]
  sizeLimit?: number
  classname?: string
  canUploadFolder?: boolean
  name?: string
  disabled?: boolean
  file?: FileProps | File
  fileType: `image` | `document` | `csv`
  onFileUpload?: (file?: File) => void
}

export default function FileUploader({
  allowedFiles,
  sizeLimit = 10,
  fileType,
  canUploadFolder = false,
  name,
  classname,
  disabled = false,
  file,
  onFileUpload,
}: FileUploaderProps) {
  let [progress, setProgress] = useState<number>(0)
  let [currentFile, setCurrentFile] = useState<FileProps>()
  let [fileLoaded, setFileLoaded] = useState<boolean>(false)
  let inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (file && file instanceof File) {
      // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
      processFile(file)
    }
  }, [])

  // The uploaded file can also change from drag/drop and remove actions.
  // oxlint-disable-next-line react/no-deriving-state-in-effects
  useEffect(() => {
    // Intentional synchronization: drag/drop and remove actions also update this local state.
    // oxlint-disable-next-line react/no-deriving-state-in-effects
    setCurrentFile(file)
  }, [file])

  useEffect(() => {
    return () => {
      if (currentFile?.src) URL.revokeObjectURL(currentFile.src)
    }
  }, [])

  let fileBrowserHandler = () => !disabled && inputRef.current?.click()

  let changeFileHandler = (event) => {
    let file = event.target.files[0]
    processFile(file)
    onFileUpload?.(file)
  }

  let processFile = (droppedFile: File) => {
    if (!droppedFile) return
    if (droppedFile?.size >= sizeLimit * 1024 * 1024) {
      notifyError(`${fileType} ${droppedFile.name} is bigger than ${sizeLimit}MB, please upload another ${fileType}`)
      return
    }
    let fileReader = new FileReader()
    fileReader.readAsArrayBuffer(droppedFile)
    fileReader.onloadstart = () => {
      setProgress(0)
      setCurrentFile((current) => ({ ...current, name: droppedFile.name, size: droppedFile.size }))
    }
    fileReader.onloadend = () => {
      let fileURL = URL.createObjectURL(droppedFile)
      setCurrentFile((current) => ({ ...current, src: fileURL }))
      setFileLoaded(true)
      onFileUpload?.(droppedFile)
    }
    fileReader.onprogress = (event) => {
      setProgress(Math.round((event.loaded / event.total) * 100))
    }
  }

  const removeHandler = () => {
    setCurrentFile(undefined)
    onFileUpload?.(undefined)
    if (inputRef.current) inputRef.current.value = ``
    setProgress(0)
  }

  const loadHandler = async () => {
    setProgress(100)
    setFileLoaded(true)
  }

  let dragEnter = (event) => {
    event.preventDefault()
  }

  let dragOver = (event) => {
    // Prevent default browser behavior (Prevent file from being opened)
    event.preventDefault()
  }

  let drop = (event) => {
    event.preventDefault()
    ;[...event.dataTransfer.items].forEach((item) => {
      if (item.kind === `file` && allowedFiles.includes(item.type)) {
        const droppedFile: File = item.getAsFile() as File
        // let inputFile = document.getElementById(`fileUploader`) as HTMLInputElement
        let inputFile = inputRef.current as HTMLInputElement
        inputFile.files = event.dataTransfer.files
        processFile(droppedFile)
        return
      }
    })
  }

  // oxlint-disable-next-line react/no-unstable-nested-components -- render helper intentionally captures parent state
  const PreviewFile = () => {
    return (
      <>
        <div
          className={classNames(
            fileLoaded ? `hidden` : ``,
            `flex h-[52px] w-[52px] items-center justify-center rounded-full bg-surface-muted p-2`,
          )}
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-surface p-2 shadow-elevation-2">
            <PageIcon className="text-strong" />
          </div>
        </div>
        <div
          className={classNames(
            fileLoaded ? `` : `hidden`,
            `h-20 w-20 rounded-lg border border-dashed border-default bg-surface p-3`,
          )}
        >
          {fileType === `image` ? (
            <img
              id="imagePreview"
              alt="Preview"
              className="h-full rounded-lg"
              src={currentFile?.src}
              height={55}
              width={55}
              onLoad={loadHandler}
              onError={() => {
                setCurrentFile(undefined)
              }}
            />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-surface-subtle">
              <PageIcon className="text-strong" />
            </div>
          )}
        </div>
        <div className="flex h-20 w-full flex-col justify-around gap-2 text-ui-sm font-medium">
          {currentFile && (
            <div className="flex justify-between">
              <span className="text-strong">{currentFile.name}</span>
              <XMarkIcon
                className={classNames(fileLoaded ? `hidden` : ``, `h-5 w-5 cursor-pointer`)}
                onClick={() => setCurrentFile(undefined)}
              />
            </div>
          )}
          {currentFile?.size && (
            <div className=" text-subtle">
              <span>{`${Math.round(currentFile.size / 1048 / 1048)} MB`}</span>
              <span className={classNames(fileLoaded ? `` : `hidden`)}>
                {` ∙ Uploaded ${formatDate({ value: moment.utc(), format: `D MMMM YYYY` })} `}
              </span>
            </div>
          )}
          {fileLoaded ? (
            <div className="flex justify-start gap-x-6">
              <button
                className="text-ui-sm text-strong disabled:cursor-not-allowed"
                type="button"
                onClick={fileBrowserHandler}
                disabled={disabled}
              >
                Replace
              </button>
              <button
                className="text-ui-sm text-strong disabled:cursor-not-allowed"
                type="button"
                onClick={removeHandler}
                disabled={disabled}
              >
                Remove
              </button>
            </div>
          ) : (
            <ProgressBar progress={progress} />
          )}
        </div>
      </>
    )
  }

  // oxlint-disable-next-line react/no-unstable-nested-components -- render helper intentionally captures parent state
  const UploadFile = () => {
    return (
      <>
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-surface-muted p-2">
          <PlusIcon
            strokeWidth={1.5}
            className={classNames(
              disabled ? `bg-surface-subtle hover:cursor-not-allowed` : `bg-surface hover:cursor-pointer`,
              `rounded-full p-1 text-body-xs text-strong shadow-elevation-2`,
            )}
            onClick={fileBrowserHandler}
          />
        </div>
        <div className="flex w-full flex-col gap-2">
          <span className="text-ui-md text-strong">Drag and drop your file here</span>
          {canUploadFolder && <span className="text-body-sm text-subtle">or select a folder</span>}
          <span className="text-body-sm text-subtle">Upload a CSV file</span>
        </div>
      </>
    )
  }

  return (
    <div
      id="drop_zone"
      className={classNames(`px-4`, classname)}
      onDragEnter={(event) => {
        if (disabled) return null
        dragEnter(event)
      }}
      onDragOver={(event) => {
        if (disabled) return null
        dragOver(event)
      }}
      onDrop={(event) => {
        if (disabled) return null
        drop(event)
      }}
    >
      <div className="flex flex-row items-center justify-start gap-4 rounded-lg border border-dashed border-default bg-surface-subtle p-6">
        {currentFile?.name ? <PreviewFile /> : <UploadFile />}
        <input
          id="fileUploader"
          disabled={disabled}
          name={name ?? `file`}
          type="file"
          className="hidden"
          ref={inputRef}
          accept={allowedFiles?.join(`,`)}
          onChange={changeFileHandler}
        />
      </div>
    </div>
  )
}
